import Bugsnag from '@bugsnag/js';
import type { Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { isAxiosError } from 'axios';

import { isExpectedNetworkAxiosError } from '~/common/utils/isExpectedNetworkAxiosError';

const EXPECTED_NETWORK_ERROR_MESSAGES: ReadonlyArray<string> = [
  'Request aborted',
  'Network Error',
  'Timeout error',
];
const AXIOS_TIMEOUT_MESSAGE = /^timeout (of \d+ms )?exceeded$/;
const RESIZE_OBSERVER_LOOP_MESSAGE = /^ResizeObserver loop/;
const RECAPTCHA_TIMEOUT_MESSAGE = /^reCAPTCHA Timeout/;
const EXPECTED_TYPE_ERROR_MESSAGES: ReadonlyArray<string> = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

const isExpectedNetworkError = (event: Event) => {
  if (isAxiosError(event.originalError)) {
    return isExpectedNetworkAxiosError(event.originalError);
  }

  const firstError = event.errors[0];
  return (
    firstError?.errorClass === 'AxiosError' &&
    (EXPECTED_NETWORK_ERROR_MESSAGES.includes(firstError.errorMessage) ||
      AXIOS_TIMEOUT_MESSAGE.test(firstError.errorMessage))
  );
};

const UNSAVED_OFFER_ROUTE_ABORT_MESSAGE = 'Route change aborted: unsaved offer settings';

const isIntentionalRouteCancellation = (event: Event): boolean => {
  const error: unknown = event.originalError;
  return (
    error instanceof Error &&
    error.message === UNSAVED_OFFER_ROUTE_ABORT_MESSAGE &&
    'cancelled' in error &&
    error.cancelled === true
  );
};

const isBrowserNoise = (event: Event): boolean => {
  const firstError = event.errors[0];
  if (!firstError) {
    return false;
  }

  return (
    RESIZE_OBSERVER_LOOP_MESSAGE.test(firstError.errorMessage) ||
    RECAPTCHA_TIMEOUT_MESSAGE.test(firstError.errorMessage) ||
    (firstError.errorClass === 'TypeError' &&
      EXPECTED_TYPE_ERROR_MESSAGES.includes(firstError.errorMessage))
  );
};

const initBugSnag = () => {
  if (process.env.NEXT_PUBLIC_BUGSNAG_ENABLED !== 'true') {
    return;
  }

  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NEXT_PUBLIC_ENV,
    appVersion: process.env.VERSION,
    // appType: config.BUILD_TARGET,
    trackInlineScripts: false,
    onError: (event) =>
      !isExpectedNetworkError(event) &&
      !isBrowserNoise(event) &&
      !isIntentionalRouteCancellation(event),
  });
};

const isBugSnagInitialised = () => Bugsnag.isStarted();

export { initBugSnag, isBrowserNoise, isBugSnagInitialised, isExpectedNetworkError };
